/**
 * `schemas/common.ts` — the pieces every other wire schema is built out of.
 *
 * Contract source: `spec.md` §6.1 (conventions), §6.2 (the closed error-code set).
 *
 * Three conventions hold across `schemas/*`:
 *
 *  1. **The zod schema is the source of truth.** TypeScript types are always
 *     `z.infer<typeof X>` — never hand-declared a second time. Where the plan names a
 *     schema without a `Schema` suffix (`ErrorCode`, `UnixSeconds`, `Ms`, `TrackId`,
 *     `ShortId`) the name is declared twice on purpose: once as a `const` (the schema)
 *     and once as a `type` (its inferred output). TypeScript merges a value and a type
 *     of the same name, so `UnixSeconds` is usable in both positions.
 *  2. **Request bodies are `z.strictObject`** (an unexpected key is a bug worth a 400),
 *     **querystrings and path params are `z.object`** (unknown query keys are stripped,
 *     because analytics and Telegram both like to append them), and **responses are
 *     `z.strictObject`** so the round-trip test in `__tests__/round-trip.test.ts` is a
 *     real equality check rather than a subset check.
 *  3. **Querystring scalars coerce.** A querystring arrives as strings. Fastify's ajv is
 *     configured with `coerceTypes` so the JSON Schema layer usually converts first, but
 *     a route that parses `request.query` directly must not break, so the numeric and
 *     boolean param helpers accept both the string and the already-coerced form.
 *
 * `toFastifySchema()` (bottom of this file) is how a route gets the JSON Schema half of
 * §6.1's "validated by a Fastify JSON schema **and** the corresponding zod schema"
 * without the two drifting: there is only one declaration, and the JSON Schema is
 * generated from it.
 */
import * as z from 'zod';
/* ────────────────────────────────────────────────────────────────────────────
 * Error codes — spec.md §6.2. CLOSED SET.
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Every `error.code` the API may emit, in the order the spec's table lists them.
 * `__tests__/error-codes.test.ts` extracts this list straight out of `docs/spec.md`
 * §6.2 and diffs it both ways, so this array cannot silently gain or lose a member.
 */
export const ERROR_CODES = [
    'BAD_REQUEST',
    'BAD_CURSOR',
    'NO_SESSION',
    'SESSION_EXPIRED',
    'BAD_SESSION',
    'INITDATA_BAD_HASH',
    'INITDATA_BAD_SIGNATURE',
    'INITDATA_EXPIRED',
    'INITDATA_FUTURE',
    'INITDATA_NO_USER',
    'FORBIDDEN',
    'USER_BLOCKED',
    'NOT_SUBSCRIBED',
    'MEDIA_TOKEN_MISSING',
    'MEDIA_TOKEN_MALFORMED',
    'MEDIA_TOKEN_EXPIRED',
    'MEDIA_TOKEN_BAD_SIG',
    'NOT_FOUND',
    'CONFLICT',
    'TRACK_UNAVAILABLE',
    'RANGE_NOT_SATISFIABLE',
    'SEARCH_EMPTY_QUERY',
    'RATE_LIMITED',
    'INTERNAL',
    'TELEGRAM_ERROR',
    'UPSTREAM_THROTTLED',
    'INDEX_NOT_READY',
];
/** The closed `error.code` enum. Also usable as a type (see convention 1 above). */
export const ErrorCode = z.enum(ERROR_CODES);
/** Alias so a caller that reaches for the uniform `*Schema` spelling finds it. */
export const ErrorCodeSchema = ErrorCode;
/**
 * The HTTP status each code is returned with (`spec.md` §6.2, column 1).
 * The error mapper in `apps/api` (T20) is expected to use exactly this table so that a
 * code cannot be returned under two different statuses in two different routes.
 */
export const ERROR_HTTP_STATUS = Object.freeze({
    BAD_REQUEST: 400,
    BAD_CURSOR: 400,
    NO_SESSION: 401,
    SESSION_EXPIRED: 401,
    BAD_SESSION: 401,
    INITDATA_BAD_HASH: 401,
    INITDATA_BAD_SIGNATURE: 401,
    INITDATA_EXPIRED: 401,
    INITDATA_FUTURE: 401,
    INITDATA_NO_USER: 401,
    FORBIDDEN: 403,
    USER_BLOCKED: 403,
    NOT_SUBSCRIBED: 403,
    MEDIA_TOKEN_MISSING: 403,
    MEDIA_TOKEN_MALFORMED: 403,
    MEDIA_TOKEN_EXPIRED: 403,
    MEDIA_TOKEN_BAD_SIG: 403,
    NOT_FOUND: 404,
    CONFLICT: 409,
    TRACK_UNAVAILABLE: 410,
    RANGE_NOT_SATISFIABLE: 416,
    SEARCH_EMPTY_QUERY: 422,
    RATE_LIMITED: 429,
    INTERNAL: 500,
    TELEGRAM_ERROR: 502,
    UPSTREAM_THROTTLED: 503,
    INDEX_NOT_READY: 503,
});
/* ────────────────────────────────────────────────────────────────────────────
 * Scalar refinements
 * ──────────────────────────────────────────────────────────────────────────── */
/** Unix timestamp in **seconds** (spec.md §6.1: "Timestamps are unix seconds"). */
export const UnixSeconds = z.int().nonnegative();
/** A duration or position in **milliseconds** — the unit the player speaks. */
export const Ms = z.int().nonnegative();
/** `tracks.id`. */
export const TrackId = z.int().positive();
/** Any surrogate `INTEGER PRIMARY KEY` (artists, tags, playlists, queue items, jobs). */
export const RowId = z.int().positive();
/** A Telegram user id. */
export const UserId = z.int().positive();
/** `tracks.short_id` / `playlists.short_id`: 8 deep-link-safe base64url characters. */
export const ShortId = z.string().regex(/^[A-Za-z0-9_-]{8}$/, {
    error: 'shortId must be 8 base64url characters',
});
/** `playlists.share_slug`: 10 base64url characters, minted only while `is_public`. */
export const ShareSlug = z.string().regex(/^[A-Za-z0-9_-]{10}$/, {
    error: 'shareSlug must be 10 base64url characters',
});
/**
 * A `startapp` payload. Mirrors `isValidStartParam` in `ids.ts` (T07) — the same regex
 * on purpose: Telegram allows only these characters in `startapp`.
 */
export const StartParam = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/, {
    error: 'startParam must be 1-64 base64url characters',
});
/**
 * A display string that must never be the empty string. `spec.md` §11.9 is emphatic
 * that an unknown value is `null`, never `''`, `'—'`, `'Unknown'` or `'Various'`.
 */
export const NonEmptyText = z.string().min(1);
/** `NonEmptyText | null` — the shape of every "may be genuinely unknown" text field. */
export const NullableText = NonEmptyText.nullable();
/**
 * A TL `long` carried as a decimal string. `size_bytes`, `tg_doc_id`, `tg_access_hash`
 * and `tg_grouped_id` are all TEXT in the DDL because they exceed 2^53 (§5.2).
 */
export const TlLongString = z.string().regex(/^\d{1,20}$/, {
    error: 'expected a TL long as a decimal string',
});
/** `tracks.size_bytes`. */
export const SizeBytes = TlLongString;
/** `tracks.catalogue_no`, e.g. `BL-0421` (design-direction.md §6.5). */
export const CatalogueNo = z.string().regex(/^BL-\d{4}$/, {
    error: 'catalogueNo must look like BL-0421',
});
/** Standard (non-url) base64, the encoding used for peaks and inline blur thumbs. */
export const Base64 = z.string().regex(/^[A-Za-z0-9+/]*={0,2}$/, {
    error: 'expected base64',
});
/** An unsigned 32-bit integer — plate seeds and the shuffle seed. */
export const Uint32 = z.int().min(0).max(4294967295);
/**
 * Session token, `spec.md` §9.2: `${userId}.${exp}.${jti}.${sig}` — exactly four
 * `.`-separated parts.
 */
export const SessionToken = z
    .string()
    .regex(/^\d{1,19}\.\d{1,19}\.[A-Za-z0-9_-]{1,64}\.[A-Za-z0-9_-]{1,128}$/, {
    error: 'session token must be userId.exp.jti.sig',
});
/**
 * Media URL token, `spec.md` §9.4: `${exp}.${userId}.${sig}` — exactly three parts.
 * Note the field order differs from the session token; that is the spec's, not a typo.
 */
export const MediaToken = z.string().regex(/^\d{1,19}\.\d{1,19}\.[A-Za-z0-9_-]{1,128}$/, {
    error: 'media token must be exp.userId.sig',
});
/* ────────────────────────────────────────────────────────────────────────────
 * Cursor pagination — spec.md §6.1
 * ──────────────────────────────────────────────────────────────────────────── */
/** `spec.md` §6.1: a cursor is opaque base64url and at most 200 bytes. */
export const MAX_CURSOR_CHARS = 200;
export const CursorSchema = z
    .string()
    .min(1)
    .max(MAX_CURSOR_CHARS)
    .regex(/^[A-Za-z0-9_-]+$/, {
    error: 'cursor must be base64url',
});
/* ────────────────────────────────────────────────────────────────────────────
 * Querystring / path-param helpers
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * A boolean query parameter.
 *
 * Accepts a real boolean (Fastify's ajv has already coerced it) **or** the textual
 * forms a client may send. Deliberately NOT `z.stringbool()`, which rejects an actual
 * boolean and would therefore fail on exactly the ajv-coerced path we normally take;
 * and deliberately not `z.coerce.boolean()`, for which `Boolean('false') === true`.
 */
export const BooleanParam = z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0', 'yes', 'no', 'on', 'off'])])
    .transform((v) => typeof v === 'boolean' ? v : v === 'true' || v === '1' || v === 'yes' || v === 'on');
/** An integer query parameter, tolerant of the string form. */
export const IntParam = z.coerce.number().int();
/** A positive-integer query or path parameter (ids). */
export const PositiveIntParam = z.coerce.number().int().positive();
/** A non-negative integer query parameter. */
export const NonNegativeIntParam = z.coerce.number().int().nonnegative();
/**
 * A `RANDOM()` stability seed (`spec.md` §6.4). Any 32-bit integer; the query layer
 * folds it into the ORDER BY so a paged random listing does not reshuffle.
 */
export const SeedParam = z.coerce.number().int().min(0).max(4294967295);
/**
 * `limitParam(min, max, fallback)` — the paging `limit` in the exact bounds §6 gives
 * for each endpoint. `.default()` is applied to the coerced value, so Fastify's
 * `useDefaults` and zod agree on the result.
 */
export function limitParam(min, max, fallback) {
    return z.coerce.number().int().min(min).max(max).default(fallback);
}
/** `{ id }` where `:id` is numeric. Used by every `/:id` route with a numeric key. */
export const NumericIdParams = z.object({ id: PositiveIntParam });
/** `{ trackId }` for `/:trackId` routes (favorites, stream, cover). */
export const TrackIdParams = z.object({ trackId: PositiveIntParam });
/**
 * `GET /api/tracks/:id` accepts "either a numeric id or a `shortId` (disambiguated by
 * `/^\d+$/`)" — §6.4. This is the raw reference, still a string; the route decides.
 */
export const TrackRef = z.string().regex(/^(?:\d{1,15}|[A-Za-z0-9_-]{8})$/, {
    error: 'expected a numeric track id or an 8-character shortId',
});
export const TrackRefParams = z.object({ id: TrackRef });
/** `?t=<token>` — the querystring of every `mediatoken`-authenticated route. */
export const MediaTokenQuerySchema = z.object({ t: MediaToken });
/** The bare cursor-only querystring, for endpoints that take nothing else. */
export const CursorQuerySchema = z.object({ cursor: CursorSchema.optional() });
/* ────────────────────────────────────────────────────────────────────────────
 * Response envelope — spec.md §6.1
 * ──────────────────────────────────────────────────────────────────────────── */
export const ApiErrorSchema = z.strictObject({
    code: ErrorCode,
    message: z.string(),
    details: z.unknown().optional(),
});
/** `{ ok: false, error: { code, message, details? } }`. */
export const ErrSchema = z.strictObject({
    ok: z.literal(false),
    error: ApiErrorSchema,
});
/** `OkSchema(dataSchema)` → `{ ok: true, data }`. */
export function OkSchema(data) {
    return z.strictObject({ ok: z.literal(true), data });
}
/** `apiResponseSchema(dataSchema)` → the full `Ok | Err` discriminated union. */
export function apiResponseSchema(data) {
    return z.discriminatedUnion('ok', [OkSchema(data), ErrSchema]);
}
/* ────────────────────────────────────────────────────────────────────────────
 * Page envelope — spec.md §6.1
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * `PageSchema(itemSchema)` → `{ items, nextCursor, total? }`.
 *
 * `total` is optional because §6.1 is explicit: it "is present **only** when it is a
 * real `COUNT(*)`; it is omitted rather than estimated". Endpoints that always do the
 * count (`/api/catalogue`, `/api/favorites`, `/api/artists`, `/api/tags`) re-declare it
 * as required via `.extend({ total: TotalCount })`.
 */
export function PageSchema(item) {
    return z.strictObject({
        items: z.array(item),
        nextCursor: CursorSchema.nullable(),
        total: z.int().nonnegative().optional(),
    });
}
/** A real `COUNT(*)`. Never estimated. */
export const TotalCount = z.int().nonnegative();
/* ────────────────────────────────────────────────────────────────────────────
 * Bodyless / contentless responses
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * The minimal `200` body for the handful of endpoints where §6 states the status code
 * and nothing about the payload (`POST /api/admin/tracks/:id`, `.../refresh-ref`,
 * `.../users/:id/block`, `.../reindex/:jobId/cancel`,
 * `DELETE /api/playlists/:id/tracks/:trackId`). It is deliberately tiny and strict: an
 * invented richer shape here would be a contract the spec never asked for.
 */
export const AckSchema = z.strictObject({ done: z.literal(true) });
/** The body of a `204 No Content` response: there isn't one. */
export const NoContentSchema = z.undefined();
/**
 * Generate the Fastify/ajv JSON Schema for a request schema.
 *
 * `spec.md` §6.1 requires both validators; this keeps them from drifting by deriving
 * one from the other. Notes on the options:
 *
 * - `io: 'input'` is **mandatory**. Several request schemas end in a `.transform()`
 *   (`BooleanParam`, the search `groups` comma list) and the output side of a transform
 *   cannot be represented in JSON Schema at all — zod throws "Transforms cannot be
 *   represented in JSON Schema". The input side is exactly what ajv must validate.
 * - `target: 'draft-7'` matches the dialect Fastify's default ajv instance compiles.
 * - `unrepresentable: 'any'` degrades an unconstrainable leaf to `{}` instead of
 *   throwing; the zod layer still enforces it.
 * - `reused: 'inline'` keeps the output a single self-contained fragment, because a
 *   `$defs`/`$ref` pair would dangle once the fragment is embedded under
 *   `schema.querystring`.
 * - `$schema` is stripped: the result is a *fragment* nested inside a Fastify route
 *   schema, and a dialect declaration is meaningless (and in strict ajv, noisy) there.
 *
 * NOT generated with `zod-to-json-schema`, despite that package being in
 * `packages/shared/package.json`: version 3.25.2 walks `def.typeName`, which only zod 3
 * defines, so every zod 4 schema converts to an empty `{}` — a validator that accepts
 * everything. See the report accompanying T08.
 */
export function toFastifySchema(schema) {
    const generated = z.toJSONSchema(schema, {
        target: 'draft-7',
        io: 'input',
        unrepresentable: 'any',
        cycles: 'ref',
        reused: 'inline',
    });
    const fragment = { ...generated };
    delete fragment['$schema'];
    return fragment;
}
/**
 * Convenience wrapper: turn the zod schemas of one route into the object Fastify wants
 * at `{ schema: … }`. Keys whose zod schema was not supplied are omitted entirely
 * rather than set to `undefined` (`exactOptionalPropertyTypes` is on, and Fastify
 * treats a present-but-undefined key as a schema).
 */
export function toFastifyRouteSchema(parts) {
    const out = {};
    if (parts.params !== undefined)
        out['params'] = toFastifySchema(parts.params);
    if (parts.querystring !== undefined)
        out['querystring'] = toFastifySchema(parts.querystring);
    if (parts.body !== undefined)
        out['body'] = toFastifySchema(parts.body);
    if (parts.headers !== undefined)
        out['headers'] = toFastifySchema(parts.headers);
    return out;
}
//# sourceMappingURL=common.js.map